import React, { FC } from "react";
import styled from "styled-components";
import { observer } from "mobx-react";
import CheckoutFormSection from "./CheckoutFormSection";
import CheckoutIFrame from "./CheckoutIFrame";
import {
  CheckoutDetailSection,
  CheckoutPaymentWrapper,
  ProductDetailWrapper,
} from "../../../shared/styles";
import {
  CollapsiblePanel,
  CustomLabel,
  FlexContainer,
  Input,
  IOption,
  SingleSelect,
  CreatableSelect as SelectCreateable,
  CreatableSelect,
} from "@mindmemobile/mm-shared-global";
import { CheckOutStore } from "../../../shared/store";
import {
  CheckoutOptionsPageModel,
  CheckoutPaymentFormModel,
  CheckoutPaymentPageModel,
  PaymentMethodDetails,
  MerchantAccount,
} from "../../../shared/models";
import {
  CheckOutPromoCodeSelectionType,
  PaymentMethodTypes,
  SellerReturnPolicyType,
  SellerTermsAndConditionsType,
} from "../../../shared/enums";
import { ItemHeader, SurchargePercentField } from "../../../shared/components";

/* Allowed-range hint under the surcharge row, red once the value is invalid. */
const SurchargeNote = styled.div<{ $hasError?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;

  i {
    font-size: 14px;
    color: ${(props) => (props.$hasError ? "#ff4b55" : "#6c69ca")};
  }
  label {
    font-size: 14px;
    color: ${(props) => (props.$hasError ? "#ff4b55" : "#7a8699")};
  }
`;

type IProps = {
  checkoutStore: CheckOutStore;
};

const sellerTermsAndConditionsOptions: IOption[] = Object.keys(
  SellerTermsAndConditionsType,
).map((key) => {
  return {
    label:
      SellerTermsAndConditionsType[
        key as keyof typeof SellerTermsAndConditionsType
      ],
    value: key,
  };
});

const sellerReturnPolicyOptions: IOption[] = Object.keys(
  SellerReturnPolicyType,
).map((key) => {
  return {
    label: SellerReturnPolicyType[key as keyof typeof SellerReturnPolicyType],
    value: key,
  };
});

function filterPaymentMethodBasedOnSettings(
  type: keyof typeof PaymentMethodTypes,
  selectedMerchantAccount?: MerchantAccount,
) {
  if (selectedMerchantAccount && selectedMerchantAccount.paymentSettings) {
    switch (PaymentMethodTypes[type]) {
      case PaymentMethodTypes.CreditDebitCard:
        return (
          selectedMerchantAccount.paymentSettings.cards?.isEnabled === true
        );
        break;
      case PaymentMethodTypes.Bank:
        return (
          selectedMerchantAccount.paymentSettings.bankAch?.isEnabled === true
        );
        break;
      case PaymentMethodTypes.ApplePay:
        return (
          selectedMerchantAccount.paymentSettings.applePay?.isEnabled === true
        );
        break;
      case PaymentMethodTypes.GooglePay:
        return (
          selectedMerchantAccount.paymentSettings.googlePay?.isEnabled === true
        );
        break;
      case PaymentMethodTypes.CheckWire:
        return (
          selectedMerchantAccount.paymentSettings.checkWire?.isEnabled === true
        );
        break;
    }
  }

  return false;
}

const CheckoutPayment: FC<IProps> = (props: IProps) => {
  let paymentMethods: IOption[] = [];

  Object.keys(PaymentMethodTypes).map((key) => {
    const selectedMerchantAccount = props.checkoutStore.merchantAccounts?.find(
      (x) =>
        x.merchantAccountId ===
        props.checkoutStore.detailPage.selectedMerchantAccount
          ?.merchantAccountId,
    );
    const isAllowed = filterPaymentMethodBasedOnSettings(
      key as keyof typeof PaymentMethodTypes,
      selectedMerchantAccount,
    );
    if (isAllowed) {
      paymentMethods.push({
        label: PaymentMethodTypes[key as keyof typeof PaymentMethodTypes],
        value: key,
      });
    }
  });
  const [action, setAction] = React.useState<
    keyof CheckoutPaymentFormModel | null
  >(null);
  const { paymentPageData, optionsPage } = props.checkoutStore;
  const { formData } = paymentPageData;

  /* Editing a checkout the API returned without a rate (never set, or zeroed
     by a save made with the surcharge off): keep the selected merchant
     account's rate so the field is never empty. Runs here as well as on the
     Detail step because the checkout and the merchant account list load
     independently and either can win. */
  const merchantSurchargePercentage =
    props.checkoutStore.selectedMerchantAccountPaymentSettings.surchargeDefaults()
      .surchargePercentage;
  /* Deliberately not keyed on the rate the user is editing. The fallback fires
     whenever the current rate is empty, so depending on it re-ran the fallback
     on the keystroke that cleared the box and typed the merchant rate straight
     back in - the field could not be cleared and retyped. Mount covers a
     checkout that loaded first; merchantSurchargePercentage covers the merchant
     account list arriving later. */
  React.useEffect(() => {
    props.checkoutStore.applyMerchantSurchargePercentageFallback();
  }, [merchantSurchargePercentage]);

  /* Terms and the return policy are only offered for a seller that has that
     copy. Re-runs whenever the selected seller changes, or the seller list
     reloads with different flags, so anything the seller cannot show is put
     back to "Do Not Display" instead of being saved against empty copy. */
  const selectedSellerDetails = props.checkoutStore.selectedSellerDetails;
  React.useEffect(() => {
    /* The seller list this resolves from carries no policy copy, so the copy is
       fetched per seller before anything is decided from it. */
    props.checkoutStore.loadSelectedSellerPolicies?.();
    props.checkoutStore.applySellerTermsAvailability?.();
  }, [
    selectedSellerDetails?.id,
    selectedSellerDetails?.hasTermsAndConditions,
    selectedSellerDetails?.hasReturnPolicy,
    props.checkoutStore.isSelectedSellerPolicyResolved,
  ]);

  const handleInputChange = <T extends keyof CheckoutPaymentPageModel>(
    value: CheckoutPaymentPageModel[T],
    field: T,
  ) => {
    const clonedData = new CheckoutPaymentPageModel(paymentPageData);
    if (field in formData) {
      clonedData.formData = new CheckoutPaymentFormModel({
        ...formData,
        [field]: value,
      });
    } else {
      clonedData[field] = value;
    }
    /* Switching "Add surcharge" on with no rate yet fills in the selected
       merchant account's rate and "ask donor first", so editing an existing
       checkout shows the merchant defaults instead of an empty box. The
       checkout owns the copy from here on. */
    if (field === "surchargeEnabled" && value === true) {
      /* The stored rate, not effectiveSurchargePercentage: that one is gated on
         surchargeEnabled, which is still false on the pre-change model here, so
         it would report "no rate" and overwrite a rate the user already set. */
      const hasRate = paymentPageData.surchargePercentageValue > 0;
      if (!hasRate) {
        const defaults =
          props.checkoutStore.selectedMerchantAccountPaymentSettings.surchargeDefaults();
        clonedData.surchargePercentage = defaults.surchargePercentage;
        clonedData.surchargeAskCustomerFirst =
          defaults.surchargeAskCustomerFirst;
      }
    }
    props.checkoutStore.updatePaymentPage(
      new CheckoutPaymentPageModel(clonedData),
    );
  };

  const checkBoxInput = (
    key: keyof CheckoutPaymentPageModel,
    value: boolean,
    label: string,
    disabled: boolean,
  ) => {
    return (
      <Input
        type="checkbox"
        /* Ties the rendered label to the input so clicking the text toggles the
           box - without an id there is no htmlFor and only the box itself works. */
        id={`chk${key}`}
        value={value}
        onChange={(e) => handleInputChange(e.target.checked, key)}
        label={label}
        disabled={disabled || false}
      />
    );
  };

  const singleSelect = (
    field: keyof CheckoutPaymentPageModel,
    selectedKey: string,
    className: string,
    options: IOption[],
    error: boolean,
    errorMessage: string,
  ): React.ReactNode => {
    return (
      <SingleSelect
        selectedKey={selectedKey}
        hasError={error}
        searchable={false}
        nameKeys={["label"]}
        valueKeys={["value"]}
        options={[options || []]}
        errorMessage={errorMessage}
        onChange={(option: any) => handleInputChange(option.value, field)}
        className={`dpdn-no-separator ${className}`}
      />
    );
  };

  const renderFormSection = () => {
    return (
      <CollapsiblePanel title="Form" className="checkout-labels">
        <CheckoutFormSection
          formData={props.checkoutStore.paymentPageData.formData}
          onChange={(
            field: keyof CheckoutPaymentPageModel,
            value: CheckoutPaymentFormModel,
          ) => handleInputChange(value, field)}
          sendAction={(action: keyof CheckoutPaymentFormModel) =>
            setAction(action)
          }
        />
      </CollapsiblePanel>
    );
  };

  const renderTermsSection = () => {
    /* Shown when the seller has the copy, or when this checkout already carries a choice - so a
       saved "Require Acceptance" stays visible and editable even after the seller's copy is
       emptied. Safe from flicker only because applySellerTermsAvailability now exempts the seller
       the checkout was loaded with; it resets on a seller switch, which flips these to false in the
       same pass that clears the value. */
    const showSellerTerms =
      !!selectedSellerDetails?.hasTermsAndConditions ||
      props.checkoutStore.sellerTermsWasConfigured;
    const showSellerReturnPolicy =
      !!selectedSellerDetails?.hasReturnPolicy ||
      props.checkoutStore.sellerReturnPolicyWasConfigured;
    if (!showSellerTerms && !showSellerReturnPolicy) {
      return null;
    }
    return (
      <CollapsiblePanel title="Terms" className="checkout-labels">
        <FlexContainer fxLayout="column" spacing={15}>
          {/* {checkBoxInput(
            "isUpdatesOffers",
            paymentPageData.isUpdatesOffers,
            "Opt-in for Updates / Offers",
            false
          )} */}
          {/* {checkBoxInput(
            "isAlternateCurrencies",
            paymentPageData.isAlternateCurrencies,
            "Require acceptance of terms & conditions  (update in Settings)",
            false
          )} */}
          {showSellerTerms && (
            <FlexContainer alignItems="center" spacing={15} padding="0px">
              <CustomLabel className="seller-labels">
                Seller Terms & Conditions
              </CustomLabel>
              {singleSelect(
                "sellerTermsAndConditions",
                paymentPageData.sellerTermsAndConditions,
                "seller-terms-dropdown moneyback-chkout",
                sellerTermsAndConditionsOptions,
                false,
                "",
              )}
            </FlexContainer>
          )}
          {showSellerReturnPolicy && (
            <FlexContainer
              alignItems="center"
              spacing={15}
              padding="0px 0px 10px"
            >
              <CustomLabel className="seller-labels">
                Seller Return Policy
              </CustomLabel>
              {singleSelect(
                "sellerReturnPolicy",
                paymentPageData.sellerReturnPolicy,
                "seller-terms-dropdown moneyback-chkout",
                sellerReturnPolicyOptions,
                false,
                "",
              )}
            </FlexContainer>
          )}
          {/* The panel only renders when the seller has at least one policy,
              so this is the only case left to explain. */}
          <CustomLabel className="seller-light-comments">
            Terms must exist for the selected Seller to be presented at
            Checkout. Update
            <br /> Terms for each Seller in Payment Settings.
          </CustomLabel>
          {/* {checkBoxInput(
            "allowTaxId",
            paymentPageData.allowTaxId,
            "Allow businesses to enter Tax ID's",
            false
          )} */}
          {/* {checkBoxInput(
            "isTermsConditions",
            paymentPageData.isTermsConditions,
            "Display prices in alternate currencies",
            false
          )} */}
          {/* {checkBoxInput(
            "isRefundPolicy",
            paymentPageData.isRefundPolicy,
            "Show Refund Policy",
            false
          )}
          {paymentPageData.isRefundPolicy && (
            <FlexContainer alignItems="center" spacing={15}>
              {singleSelect(
                "refundPolicyValue",
                paymentPageData.refundPolicyValue,
                "moneyback-chkout",
                [],
                false,
                ""
              )}
              {checkBoxInput(
                "requireAcceptance",
                paymentPageData.requireAcceptance,
                "Require Acceptance",
                false
              )}
            </FlexContainer>
          )} */}
        </FlexContainer>
      </CollapsiblePanel>
    );
  };

  const radioButton = (
    value: boolean,
    fieldName: keyof CheckoutPaymentPageModel,
    label: string,
    updateValue: keyof typeof CheckOutPromoCodeSelectionType,
  ) => {
    return (
      <Input
        value={value}
        id={`rdb_${fieldName}`}
        type="radio"
        label={label}
        onChange={() => handleInputChange(updateValue, fieldName)}
        className={"chkbox-no-margin"}
      />
    );
  };

  const selectedPaymentMethods = () => {
    return (
      optionsPage.selectedPaymentMethods.map((b) => {
        return {
          label: PaymentMethodTypes[b as keyof typeof PaymentMethodTypes],
          value: b,
        } as IOption;
      }) || []
    );
  };

  const onDeleteSelectedItem = (newPaymentMethods: IOption[]) => {
    props.checkoutStore.updateOptionsPage(
      new CheckoutOptionsPageModel({
        ...optionsPage,
        selectedPaymentMethods: newPaymentMethods.map((item) => item.value),
      }),
    );
  };

  //   const renderMarketingInputs = () => {
  //     return (
  //       <FlexContainer fxLayout="column" spacing={18}>
  //         {checkBoxInput(
  //           "showTimer",
  //           paymentPageData.showTimer,
  //           "Show Checkout timer",
  //           false
  //         )}
  //         {checkBoxInput(
  //           "isPromotionCodes",
  //           paymentPageData.isPromotionCodes,
  //           "Allow Promotion Codes",
  //           false
  //         )}
  //         {paymentPageData.isPromotionCodes && (
  //           <div>
  //             <FlexContainer
  //               alignItems="center"
  //               spacing={50}
  //               className={`marketing-radio-btns ${(paymentPageData.promoCodeSelectionType === CheckOutPromoCodeSelectionType.SelectCodes && "marketing-radio-spacing") || (paymentPageData.promoCodeSelectionType === CheckOutPromoCodeSelectionType.ApplyCode && "marketing-radio-spacing")}`}
  //             >
  //               {radioButton(
  //                 paymentPageData.promoCodeSelectionType ===
  //                   CheckOutPromoCodeSelectionType.AllActiveCodes,
  //                 "promoCodeSelectionType",
  //                 "All Active Codes",
  //                 CheckOutPromoCodeSelectionType.AllActiveCodes
  //               )}
  //               {radioButton(
  //                 paymentPageData.promoCodeSelectionType ===
  //                   CheckOutPromoCodeSelectionType.SelectCodes,
  //                 "promoCodeSelectionType",
  //                 "Select Codes",
  //                 CheckOutPromoCodeSelectionType.SelectCodes
  //               )}
  //               {radioButton(
  //                 paymentPageData.promoCodeSelectionType ===
  //                   CheckOutPromoCodeSelectionType.ApplyCode,
  //                 "promoCodeSelectionType",
  //                 "Apply Code",
  //                 CheckOutPromoCodeSelectionType.ApplyCode
  //               )}
  //             </FlexContainer>
  //             {paymentPageData.promoCodeSelectionType ===
  //               CheckOutPromoCodeSelectionType.SelectCodes && (
  //               <SelectCreateable
  //                 isMulti={true}
  //                 options={samplePromoCodes}
  //                 selectedOptions={selectedPromoCodes()}
  //                 // onChange={(newValue) => console.log(newValue)}
  //                 className="chkout-pay-select"
  //                 classPrefix="chkout-pay"
  //               />
  //             )}
  //             {paymentPageData.promoCodeSelectionType ===
  //               CheckOutPromoCodeSelectionType.ApplyCode &&
  //               singleSelect(
  //                 "applyCodeValue",
  //                 paymentPageData.applyCodeValue,
  //                 "moneyback-chkout",
  //                 [],
  //                 false,
  //                 ""
  //               )}
  //           </div>
  //         )}
  //         {checkBoxInput(
  //           "isTestimonial",
  //           paymentPageData.isTestimonial,
  //           "Show Testimonial Banner",
  //           false
  //         )}
  //         {paymentPageData.isTestimonial && (
  //           <SelectCreateable
  //             isMulti={true}
  //             options={samplePromoCodes}
  //             selectedOptions={selectedPromoCodes()}
  //             // onChange={(newValue) => console.log(newValue)}
  //             className="chkout-pay-select"
  //             classPrefix="chkout-pay"
  //           />
  //         )}
  //         {checkBoxInput(
  //           "isMoneyBack",
  //           paymentPageData.isMoneyBack,
  //           "Show Guarantee Banner",
  //           false
  //         )}
  //         {paymentPageData.isMoneyBack &&
  //           singleSelect(
  //             "moneyBackDuration",
  //             paymentPageData.moneyBackDuration,
  //             "moneyback-chkout",
  //             [],
  //             false,
  //             ""
  //           )}
  //       </FlexContainer>
  //     );
  //   };

  //   const renderMarketingSection = () => {
  //     return (
  //       <CollapsiblePanel
  //         title="Marketing"
  //         className="checkout-labels chkout-label-description"
  //       >
  //         <CustomLabel className="marketing-soft-text">
  //           Add available active options below from Payment Settings.
  //         </CustomLabel>
  //         {renderMarketingInputs()}
  //       </CollapsiblePanel>
  //     );
  //   };
  const renderPaymentMethodsContent = () => {
    return (
      <FlexContainer fxLayout="column" spacing={30}>
        <FlexContainer fxLayout="column">
          <CustomLabel margin="0px 0px 20px">
            Available Payment Methods
          </CustomLabel>
          <CreatableSelect
            isMulti={true}
            options={paymentMethods}
            selectedOptions={selectedPaymentMethods()}
            onChange={(newMethods) => onDeleteSelectedItem(newMethods)}
            className="payment-method-select"
            classPrefix="payment-method"
          />
        </FlexContainer>
      </FlexContainer>
    );
  };

  const renderSurchargeSection = () => {
    const surchargeError = paymentPageData.errors.find(
      (e) => e.field === "surchargePercentage",
    )?.message;
    /* A donation checkout collects donations from donors; everything else
       takes purchases from customers. */
    const isDonation = props.checkoutStore.detailPage.isDonationOnly;
    return (
      <CollapsiblePanel title="Surcharge" className="checkout-labels">
        <FlexContainer fxLayout="column" spacing={0}>
          <CustomLabel className="seller-light-comments" margin="0px 0px 18px">
            Charge a fee to customers to cover processing costs.
          </CustomLabel>
          {/* The builder's left panel is narrow, so this row wraps as whole units rather than
              letting each control compress and its label break onto a second line. */}
          <div className="surcharge-row">
            <div className="surcharge-row-item">
              {checkBoxInput(
                "surchargeEnabled",
                paymentPageData.surchargeEnabled,
                "Add surcharge",
                false,
              )}
            </div>
            {paymentPageData.surchargeEnabled && (
              <>
                <div className="surcharge-row-item">
                  <SurchargePercentField
                    value={paymentPageData.surchargePercentage}
                    hasError={paymentPageData.hasError("surchargePercentage")}
                    onChange={(percentage) =>
                      handleInputChange(percentage, "surchargePercentage")
                    }
                  />
                </div>
                <div className="surcharge-row-item">
                  <CustomLabel>
                    to every {isDonation ? "donation" : "purchase"}
                  </CustomLabel>
                </div>
                <div className="surcharge-row-item">
                  {checkBoxInput(
                    "surchargeAskCustomerFirst",
                    paymentPageData.surchargeAskCustomerFirst,
                    isDonation ? "Ask donor first" : "Ask customer first",
                    false,
                  )}
                </div>
              </>
            )}
          </div>
          {paymentPageData.surchargeEnabled &&
            paymentPageData.hasError("surchargePercentage") && (
              <CustomLabel
                fontSize="13px"
                fontWeight="500"
                color="#ef4444"
                margin="12px 0px 0px"
              >
                {surchargeError || PaymentMethodDetails.surchargeRangeMessage}
              </CustomLabel>
            )}
        </FlexContainer>
      </CollapsiblePanel>
    );
  };

  return (
    <CheckoutPaymentWrapper>
      <ProductDetailWrapper margin="0 0 25px">
        <FlexContainer>
          <CheckoutDetailSection>
            <div className="chckout-payment">
              <ItemHeader header="Payment" />
              {renderFormSection()}
              {renderTermsSection()}
              <CollapsiblePanel
                title="Payment Methods"
                className="checkout-labels"
              >
                {renderPaymentMethodsContent()}
              </CollapsiblePanel>
              {renderSurchargeSection()}
              {/* {renderMarketingSection()} */}
            </div>
          </CheckoutDetailSection>
          <CheckoutIFrame checkoutStore={props.checkoutStore} action={action} />
        </FlexContainer>
      </ProductDetailWrapper>
    </CheckoutPaymentWrapper>
  );
};

export default observer(CheckoutPayment);
